/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import OptionWrapper from "../others/option-wrapper";
import PropTypes from 'prop-types';
import { forwardRef, memo, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { animations } from "../../styles/animation";
import { bgColor, borderRadius, boxBorder, cursorMove, displayFlex, flexCol, flexRow, justifyCenter, left, parseMaxWidthHeight, parseMinWidth, parseWidthHeight, pdt, positionAbsolute, positionRelative, top } from "../../styles/general";
import { makeStyles, useTheme } from "../../theme";
import { classNames, useDelayUnmount } from "../../utils";
import ModalContext from "./context";
const {
  typography: {
    paragraph1
  },
  zIndex: zIndexCORE
} = useTheme();
const useStyles = makeStyles({
  noScroll: {
    key: props => `DGN-Modal-${props}`,
    overflow: 'hidden'
  }
});
const fadeInDown = animations.fadeInDown;
const fadeOutUp = animations.fadeOutUp;
const Modal = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  action = {},
  alignment,
  autoFocus,
  children,
  className,
  darkTheme,
  dragAnyWhere,
  id,
  moveable,
  moveOutScreen,
  onClose,
  open,
  pressESCToClose,
  style,
  width,
  zIndex
}, reference) => {
  if (!reference) reference = useRef(null);
  const ref = useRef(null);

  // const overflow = useRef(null);
  const modalBoxRef = useRef(null);
  const [openState, setOpenState] = useState(open);
  const showModal = useDelayUnmount(openState, 200);
  const _ModalContainerCSS = ModalContainerCSS(zIndex, alignment, darkTheme);
  const _ModalBoxCSS = ModalBoxCSS(width, moveable, dragAnyWhere);
  const onShowModal = () => {
    setOpenState(true);
  };
  const onCloseModal = () => {
    onClose && onClose();
    setOpenState(false);
  };
  const pressESCHandler = event => {
    if (event.key === 'Escape') {
      const modals = document.querySelectorAll('.DGN-UI-Portal');
      if ((modals === null || modals === void 0 ? void 0 : modals.length) > 1 && Array.from(modals)[modals.length - 1] && !Array.from(modals)[modals.length - 1].contains(ref.current)) {
        return;
      }
      onCloseModal();
    }
  };
  const dragMouseDown = e => {
    let pos1 = 0,
      pos2 = 0,
      pos3 = 0,
      pos4 = 0;
    pos3 = e.clientX;
    pos4 = e.clientY;
    const maxTop = window.innerHeight - modalBoxRef.current.offsetHeight;
    const maxLeft = window.innerWidth - modalBoxRef.current.offsetWidth;
    if (modalBoxRef.current.style.display !== 'block') {
      const top = modalBoxRef.current.offsetTop;
      const left = modalBoxRef.current.offsetLeft;
      modalBoxRef.current.style.display = 'block';
      modalBoxRef.current.style.position = 'absolute';
      modalBoxRef.current.style.margin = '0px';
      modalBoxRef.current.style.top = top + 'px';
      modalBoxRef.current.style.left = left + 'px';
    }
    document.onmouseup = () => {
      /* stop moving when mouse button is released:*/
      document.onmouseup = null;
      document.onmousemove = null;
    };
    // call a function whenever the cursor moves:
    document.onmousemove = el => {
      pos1 = pos3 - el.clientX;
      pos2 = pos4 - el.clientY;
      pos3 = el.clientX;
      pos4 = el.clientY;
      let newTop = modalBoxRef.current.offsetTop - pos2;
      let newLeft = modalBoxRef.current.offsetLeft - pos1;
      if (!moveOutScreen) {
        if (newTop > maxTop) {
          newTop = maxTop;
        } else if (newTop < 0) {
          newTop = 0;
        }
        if (newLeft > maxLeft) {
          newLeft = maxLeft;
        } else if (newLeft < 0) {
          newLeft = 0;
        }
      }
      // set the element's new position:
      modalBoxRef.current.style.top = newTop + 'px';
      modalBoxRef.current.style.left = newLeft + 'px';
    };
  };
  useEffect(() => {
    setOpenState(open);
  }, [open]);
  useEffect(() => {
    if (openState && ref.current) {
      const modals = document.querySelectorAll('.DGN-UI-Modal');
      if (!darkTheme && modals && modals.length > 1) {
        ref.current.style.backgroundColor = 'transparent';
      }
      if (autoFocus) {
        if (!ref.current.contains(document.activeElement)) {
          ref.current.tabIndex = -1;
          ref.current.focus();
        }
      }
      const timestamp = Date.now();
      const classes = useStyles(timestamp);
      const noScrollClass = classes.noScroll.split(' ');
      document.body.classList.add(...noScrollClass);
      // overflow.current = document.body.style.overflow; // overflow of <body /> before open modal
      // document.body.style.overflow = 'hidden';
      // Allow press ESC to close popup
      if (pressESCToClose) {
        document.addEventListener('keydown', pressESCHandler);
      }
      return () => {
        document.body.classList.remove(...noScrollClass);
        // document.body.style.overflow = overflow.current; // reset overflow of <body /> before open modal
        if (pressESCToClose) {
          document.removeEventListener('keydown', pressESCHandler);
        }
      };
    }
  }, [openState]);
  useImperativeHandle(reference, () => {
    const currentRef = ref.current || {};
    currentRef.element = ref.current;
    const _instance = {
      ...action
    }; // methods
    _instance.__proto__ = {}; // hidden methods
    currentRef.instance = _instance;
    return currentRef;
  });
  return useMemo(() => {
    if (showModal) {
      const node = jsx("div", {
        className: 'DGN-UI-Portal DGN-UI-Modal',
        style: {
          position: 'fixed',
          zIndex,
          inset: 0
        }
      }, jsx("div", {
        id: id,
        css: _ModalContainerCSS,
        className: 'DGN-UI-Modal-Container',
        ref: ref
      }, jsx("div", {
        css: _ModalBoxCSS,
        ref: modalBoxRef,
        onMouseDown: moveable && dragAnyWhere ? dragMouseDown : null,
        className: classNames('DGN-UI-Modal-Box', openState ? 'DGN-UI-Modal-Show' : 'DGN-UI-Modal-Hide', className),
        style: style
      }, jsx(ModalContext.Provider, {
        value: {
          show: onShowModal,
          close: onCloseModal,
          onMoveModal: moveable && !dragAnyWhere ? dragMouseDown : null
        }
      }, children))));
      return /*#__PURE__*/createPortal(node, document.body);
    }
    return null;
  }, [alignment, autoFocus, children, className, darkTheme, dragAnyWhere, id, moveable, moveOutScreen, onClose, open, pressESCToClose, style, width, zIndex, openState, showModal]);
}));
const ModalContainerCSS = (zIndex, alignment, darkTheme) => css`
    ${displayFlex};
    ${flexRow};
    ${positionAbsolute}
    ${justifyCenter};
    ${parseWidthHeight('100%')};
    ${pdt(alignment === 'top' ? [3] : 0)};
    ${bgColor(darkTheme ? 'rgba(21, 26, 48, 0.5)' : 'transparent')};
    ${top(0)};
    ${left(0)};
    z-index: ${zIndex};
    align-items: ${alignment === 'top' ? 'inherit' : 'center'};
`;
const ModalBoxCSS = (width, moveable, dragAnyWhere) => css`
    ${paragraph1};
    ${displayFlex};
    ${flexCol};
    ${positionRelative};
    ${borderRadius(4)};
    ${boxBorder};
    ${parseWidthHeight(width, 'max-content')};
    ${parseMaxWidthHeight('90%', 'calc(100% - 12px)')};
    ${parseMinWidth(150)};
    ${moveable && dragAnyWhere && cursorMove};
    ${bgColor('system/white')};
    margin: 0 auto;
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
    -webkit-box-orient: vertical;
    -webkit-box-direction: normal;
    animation: ${fadeInDown} 0.2s ease;
    animation-fill-mode: forwards;
    &.DGN-UI-Modal-Show {
        animation: ${fadeInDown} 0.2s ease;
    }
    &.DGN-UI-Modal-Hide {
        animation: ${fadeOutUp} 0.3s ease;
        animation-fill-mode: forwards;
    }
`;
Modal.defaultProps = {
  alignment: 'top',
  autoFocus: true,
  className: '',
  darkTheme: true,
  dragAnyWhere: false,
  moveable: false,
  moveOutScreen: false,
  open: false,
  pressESCToClose: true,
  style: {},
  width: '80%',
  zIndex: zIndexCORE()
};
Modal.propTypes = {
  /** The component alignment. */
  alignment: PropTypes.oneOf(['top', 'center']),
  /** If `true`, focus when component is shown. */
  autoFocus: PropTypes.bool,
  /** The content of the component. */
  children: PropTypes.node,
  /** Class for component. */
  className: PropTypes.string,
  /** If `true`, dark backdrop when component is shown. */
  darkTheme: PropTypes.bool,
  /** If `true`, allow drag the component at any position. */
  dragAnyWhere: PropTypes.bool,
  /** If `true`, allow move the component by drag and move. */
  moveable: PropTypes.bool,
  /** If `true`, allows drag the component out of the screen. */
  moveOutScreen: PropTypes.bool,
  /** Callback fired when the component requests to be closed. */
  onClose: PropTypes.func,
  /** If `true`, the component is shown. */
  open: PropTypes.bool,
  /** If `true`, hitting escape will close component. */
  pressESCToClose: PropTypes.bool,
  /** Style inline of component. */
  style: PropTypes.object,
  /** Width of the component. */
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** The z-index of component. */
  zIndex: PropTypes.number
};
export { Modal };
export default OptionWrapper(Modal);